import { Box, Heading } from 'lucid-components-react';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import LazyLoad from 'react-lazyload';

import Link from 'components/link/link';
import { Drupal_ParagraphPaperSection } from 'generated/graphql';
import { mqMin } from 'theme/base';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';
import { getProcessedValue } from 'utils/helpers';
import linkParser from 'utils/link-parser';

interface Props {
  list: Drupal_ParagraphPaperSection[];
}

const FeatureList = ({ list }: Props) => {
  return (
    <Box
      as="section"
      css={(theme: LucidTheme) => ({
        label: 'container',
        padding: `${theme.space[2]}em ${theme.space[0]}`,
        [mqMin.tabletSmall]: {
          padding: `${theme.space[8]}px ${theme.space[0]}`
        }
      })}
    >
      <Box css={{ label: 'wrapper' }}>
        <ul
          css={(theme: LucidTheme) => ({
            listStyle: 'none',
            margin: theme.space[0]
          })}
        >
          {list.map((item, index) => (
            <li
              key={item.fieldHeader || `feature-list-${index}`}
              css={(theme: LucidTheme) => ({
                label: 'item',
                borderBottom: `1px solid ${theme.colors.coolGrey[5]}`,
                '&:last-of-type': {
                  borderBottom: 0
                },
                [mqMin.tabletLandscape]: {
                  paddingTop: theme.space[6]
                }
              })}
            >
              <Box
                css={{
                  label: 'content',
                  display: 'flex',
                  flexDirection: 'column',
                  [mqMin.tabletLandscape]: {
                    flexDirection: 'row'
                  }
                }}
              >
                <Box
                  css={{
                    label: 'text',
                    display: 'flex',
                    flexDirection: 'column',
                    order: 2,
                    [mqMin.tabletLandscape]: {
                      order: 1,
                      flex: '0 1 50%',
                      maxWidth: '50%'
                    }
                  }}
                >
                  <Heading
                    as="h2"
                    css={{
                      order: 1
                    }}
                    mt={0}
                    variant="subheadS"
                  >
                    {item.fieldHeader}
                  </Heading>
                  {item.fieldDescriptionWysiwyg && (
                    <Text
                      css={{
                        order: 2
                      }}
                      textStyles="body"
                    >
                      {ReactHtmlParser(
                        getProcessedValue(item.fieldDescriptionWysiwyg),
                        {
                          transform: linkParser
                        }
                      )}
                    </Text>
                  )}
                  {item.fieldCta && item.fieldCta.url && (
                    <Link
                      hasArrow
                      secondary
                      css={theme => ({
                        order: 3,
                        marginBottom: theme.space[4]
                      })}
                      to={item.fieldCta.url.path || ''}
                    >
                      {item.fieldCta.title}
                    </Link>
                  )}
                </Box>
                <Box
                  css={(theme: LucidTheme) => ({
                    label: 'imageWrapper',
                    order: 1,
                    padding: `${theme.space[4]}px ${theme.space[0]}`,
                    [mqMin.tabletLandscape]: {
                      order: 2,
                      flex: '0 1 48%',
                      maxWidth: '48%',
                      marginLeft: theme.space[10]
                    }
                  })}
                >
                  <LazyLoad
                    key={item.fieldImage || `feature-list-image-${index}`}
                    offset={10}
                  >
                    <Box
                      css={{
                        label: 'image',
                        position: 'relative',
                        width: '100%',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundClip: 'content-box',
                        backgroundImage: `url(${item.fieldImage || ''})`,
                        backgroundSize: '100%',
                        '&::before': {
                          display: 'block',
                          content: '""',
                          paddingTop: '75%'
                        }
                      }}
                    />
                  </LazyLoad>
                </Box>
              </Box>
            </li>
          ))}
        </ul>
      </Box>
    </Box>
  );
};

export default FeatureList;
